(function () {
  if (customElements.get('nkdc-predictive-search')) return;

  class NkdcPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.abortController = new AbortController();
      this.activeFetch = null;
      this.activeIndex = -1;
      this.debounceTimer = null;
      this.lastRequestedQuery = '';
      this.options = [];
    }

    connectedCallback() {
      this.input = this.querySelector('[data-nkdc-input]');
      this.panel = this.querySelector('[data-nkdc-panel]');
      this.content = this.querySelector('[data-nkdc-content]');
      this.clearButton = this.querySelector('[data-nkdc-clear]');
      this.spinner = this.querySelector('[data-nkdc-spinner]');
      this.status = this.querySelector('[data-nkdc-status]');
      this.viewAll = this.querySelector('[data-nkdc-view-all]');

      if (!this.input || !this.panel || !this.content || !this.clearButton || !this.viewAll) return;

      const { signal } = this.abortController;
      this.input.addEventListener('input', this.handleInput, { signal });
      this.input.addEventListener('keydown', this.handleKeydown, { signal });
      this.input.addEventListener('focus', this.handleFocus, { signal });
      this.clearButton.addEventListener('click', this.clear, { signal });
      this.addEventListener('focusout', this.handleFocusOut, { signal });
      document.addEventListener('pointerdown', this.handleOutsidePointer, { signal });
    }

    disconnectedCallback() {
      window.clearTimeout(this.debounceTimer);
      this.activeFetch?.abort();
      this.abortController.abort();
    }

    get instanceId() {
      return this.dataset.instanceId || this.id || 'nkdc-predictive-search';
    }

    get minQueryLength() {
      return Number.parseInt(this.dataset.minQueryLength || '2', 10);
    }

    get resultsLimit() {
      return Number.parseInt(this.dataset.resultsLimit || '4', 10);
    }

    handleInput = () => {
      const query = this.input.value.trim();
      this.clearButton.hidden = query.length === 0;
      window.clearTimeout(this.debounceTimer);
      this.activeFetch?.abort();

      if (query.length < this.minQueryLength) {
        this.lastRequestedQuery = '';
        this.close();
        this.announce(query.length ? `Enter at least ${this.minQueryLength} characters to search.` : '');
        return;
      }

      this.debounceTimer = window.setTimeout(() => this.fetchResults(query), 220);
    };

    handleFocus = () => {
      if (this.input.value.trim() === this.lastRequestedQuery && this.content.childElementCount > 0) {
        this.open();
      }
    };

    handleFocusOut = (event) => {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement) && !this.contains(event.relatedTarget)) {
          this.cancelPending();
          this.close();
        }
      }, 0);
    };

    handleOutsidePointer = (event) => {
      if (!this.contains(event.target)) {
        this.cancelPending();
        this.close();
      }
    };

    handleKeydown = (event) => {
      if (event.key === 'Escape') {
        if (!this.panel.hidden) {
          event.preventDefault();
          this.cancelPending();
          this.close();
          this.input.focus();
        }
        return;
      }

      if (event.key === 'Enter' && this.activeIndex < 0) {
        event.preventDefault();
        this.input.form?.requestSubmit();
        return;
      }

      if (this.panel.hidden || this.options.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.setActive(this.activeIndex < this.options.length - 1 ? this.activeIndex + 1 : 0);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.setActive(this.activeIndex > 0 ? this.activeIndex - 1 : this.options.length - 1);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.options[this.activeIndex]?.click();
      }
    };

    clear = () => {
      window.clearTimeout(this.debounceTimer);
      this.activeFetch?.abort();
      this.input.value = '';
      this.clearButton.hidden = true;
      this.lastRequestedQuery = '';
      this.content.replaceChildren();
      this.close();
      this.announce('Search cleared.');
      this.input.focus();
    };

    async fetchResults(query) {
      this.activeFetch?.abort();
      const requestController = new AbortController();
      this.activeFetch = requestController;
      this.updateViewAll(query);
      this.setLoading(true);
      this.announce(`Searching for ${query}.`);

      if (this.content.childElementCount === 0) {
        this.renderMessage('Searching…');
        this.open();
      }

      const url = new URL(this.dataset.predictiveSearchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', this.dataset.resultsSectionId || 'predictive-search');
      url.searchParams.set('resources[type]', 'product,collection');
      url.searchParams.set('resources[limit]', String(this.resultsLimit));
      url.searchParams.set('resources[limit_scope]', 'each');

      try {
        const response = await fetch(url.toString(), {
          signal: requestController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);

        const html = await response.text();
        if (requestController.signal.aborted || query !== this.input.value.trim()) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const resultsInner = documentFragment.querySelector('.predictive-search-results__inner');
        if (!resultsInner) throw new Error('Predictive search response did not include results markup');

        this.content.replaceChildren(...Array.from(resultsInner.cloneNode(true).childNodes));
        this.makeIdsInstanceSafe();
        this.setupOptions();
        const resultCount = this.options.length;
        this.lastRequestedQuery = query;
        this.updateViewAll(query);
        this.open();
        this.announce(resultCount === 1 ? `1 result for ${query}.` : `${resultCount} results for ${query}.`);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.lastRequestedQuery = query;
        this.renderMessage('Search is temporarily unavailable. Press Enter to view the full search page.');
        this.updateViewAll(query);
        this.open();
        this.announce('Predictive search is temporarily unavailable.');
      } finally {
        if (this.activeFetch === requestController) this.setLoading(false);
      }
    }

    renderMessage(message) {
      const paragraph = document.createElement('p');
      paragraph.className = 'nkdc-predictive-search__message';
      paragraph.textContent = message;
      this.content.replaceChildren(paragraph);
      this.options = [];
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    makeIdsInstanceSafe() {
      const idMap = new Map();
      this.content.querySelectorAll('[id]').forEach((element, index) => {
        const oldId = element.id;
        const newId = `${this.instanceId}-remote-${index}-${oldId}`;
        idMap.set(oldId, newId);
        element.id = newId;
      });

      ['aria-controls', 'aria-describedby', 'aria-labelledby', 'for'].forEach((attribute) => {
        this.content.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const value = element.getAttribute(attribute);
          if (!value) return;
          element.setAttribute(
            attribute,
            value
              .split(/\s+/)
              .map((token) => idMap.get(token) || token)
              .join(' ')
          );
        });
      });
    }

    setupOptions() {
      const candidates = Array.from(
        this.content.querySelectorAll('.predictive-search-results__pill[href], .resource-card__link[href]')
      );

      this.options = candidates.filter((option, index) => candidates.indexOf(option) === index);
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      this.options.forEach((option, index) => {
        option.id = `${this.instanceId}-option-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.setAttribute('tabindex', '-1');
        option.addEventListener('pointermove', () => this.setActive(index), { passive: true });
      });
    }

    setActive(index) {
      this.options.forEach((option, optionIndex) => {
        option.setAttribute('aria-selected', optionIndex === index ? 'true' : 'false');
      });

      this.activeIndex = index;
      const activeOption = this.options[index];
      if (!activeOption) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }

      this.input.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({
        block: 'nearest',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    updateViewAll(query) {
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      this.viewAll.href = url.toString();
    }

    cancelPending() {
      window.clearTimeout(this.debounceTimer);
      this.activeFetch?.abort();
      this.setLoading(false);
    }

    setLoading(isLoading) {
      if (this.spinner) this.spinner.hidden = !isLoading;
      this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.options.forEach((option) => option.setAttribute('aria-selected', 'false'));
      this.activeIndex = -1;
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  customElements.define('nkdc-predictive-search', NkdcPredictiveSearch);
})();

